import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Trash2, Search, Download, RefreshCw, Filter, X, AlertTriangle, CheckSquare, Square, Package, Database } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import apiClient from '@/services/apiClient';

const SFGPutAwayEntriesReport = () => {
  const navigate = useNavigate();
  
  // Entries state
  const [entries, setEntries] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isBackfilling, setIsBackfilling] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const [selectedEntries, setSelectedEntries] = useState(new Set());
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [entryToDelete, setEntryToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  
  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(500); // Increased from 200 to 500
  
  // Filters
  const [filters, setFilters] = useState({
    bundle_code: '',
    bin_code: '',
    design_name: '',
    barcode: '',
    date_from: '',
    date_to: '',
    status: 'all',
    source: 'all'
  });
  const [showFilters, setShowFilters] = useState(true);

  const fetchEntries = useCallback(async () => {
    setIsLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.bundle_code) params.append('bundle_code', filters.bundle_code);
      if (filters.bin_code) params.append('bin_code', filters.bin_code);
      if (filters.design_name) params.append('design_name', filters.design_name);
      if (filters.barcode) params.append('barcode', filters.barcode);
      if (filters.date_from) params.append('date_from', filters.date_from);
      if (filters.date_to) params.append('date_to', filters.date_to);
      if (filters.status && filters.status !== 'all') params.append('status', filters.status);
      if (filters.source && filters.source !== 'all') params.append('source', filters.source);
      params.append('limit', pageSize.toString());
      params.append('skip', ((currentPage - 1) * pageSize).toString());
      
      const response = await apiClient.get(`/wms/sfg/put-away-entries?${params.toString()}`);
      
      if (response.data.success) {
        setEntries(response.data.entries || []);
        setTotalCount(response.data.total || 0);
      }
    } catch (error) {
      console.error('Error fetching entries:', error);
      toast.error('Failed to load put away entries');
    } finally {
      setIsLoading(false);
    }
  }, [filters, currentPage, pageSize]);

  useEffect(() => {
    fetchEntries();
  }, [fetchEntries]);

  const handleSearch = () => {
    setSelectedEntries(new Set());
    setCurrentPage(1); // Reset to page 1 when searching
    fetchEntries();
  };

  const handleClearFilters = () => {
    setFilters({
      bundle_code: '',
      bin_code: '',
      design_name: '',
      barcode: '',
      date_from: '',
      date_to: '',
      status: 'all',
      source: 'all'
    });
    setCurrentPage(1); // Reset to page 1
  };
  
  const totalPages = Math.ceil(totalCount / pageSize);
  
  const handlePrevPage = () => {
    if (currentPage > 1) {
      setCurrentPage(prev => prev - 1);
    }
  };
  
  const handleNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(prev => prev + 1);
    }
  };

  const handleSelectEntry = (bundleCode) => {
    setSelectedEntries(prev => {
      const newSet = new Set(prev);
      if (newSet.has(bundleCode)) {
        newSet.delete(bundleCode);
      } else {
        newSet.add(bundleCode);
      }
      return newSet;
    });
  };

  const handleSelectAll = () => {
    if (selectedEntries.size === entries.length) {
      setSelectedEntries(new Set());
    } else {
      setSelectedEntries(new Set(entries.map(e => e.bundle_code)));
    }
  };

  const handleDeleteClick = (entry) => {
    setEntryToDelete(entry);
    setDeleteDialogOpen(true);
  };

  const handleBulkDeleteClick = () => {
    if (selectedEntries.size === 0) {
      toast.error('No entries selected');
      return;
    }
    setEntryToDelete(null);
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    setIsDeleting(true);
    try {
      if (entryToDelete) {
        await apiClient.delete(`/wms/sfg/put-away-entries/${encodeURIComponent(entryToDelete.bundle_code)}`);
        toast.success('Entry deleted successfully');
      } else {
        await apiClient.delete('/wms/sfg/put-away-entries/bulk', {
          data: { bundle_codes: Array.from(selectedEntries) }
        });
        toast.success(`Deleted ${selectedEntries.size} entries successfully`);
        setSelectedEntries(new Set());
      }
      setDeleteDialogOpen(false);
      fetchEntries();
    } catch (error) {
      console.error('Error deleting:', error);
      toast.error(error.response?.data?.detail || 'Failed to delete entries');
    } finally {
      setIsDeleting(false);
    }
  };

  const handleExportCSV = async () => {
    // Build query string matching the current filters (ignore pagination so we export ALL)
    const params = new URLSearchParams();
    if (filters.bundle_code) params.append('bundle_code', filters.bundle_code);
    if (filters.bin_code) params.append('bin_code', filters.bin_code);
    if (filters.design_name) params.append('design_name', filters.design_name);
    if (filters.barcode) params.append('barcode', filters.barcode);
    if (filters.date_from) params.append('date_from', filters.date_from);
    if (filters.date_to) params.append('date_to', filters.date_to);
    if (filters.status && filters.status !== 'all') params.append('status', filters.status);
    if (filters.source && filters.source !== 'all') params.append('source', filters.source);

    try {
      toast.info(`Preparing export of ${totalCount.toLocaleString()} entries...`);
      const response = await apiClient.get(
        `/wms/sfg/put-away-entries/export?${params.toString()}`,
        { responseType: 'blob' }
      );
      const blob = new Blob([response.data], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = `sfg_put_away_entries_${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(link.href);
      toast.success('Report exported successfully');
    } catch (error) {
      console.error('Error exporting report:', error);
      toast.error(error?.response?.data?.detail || 'Failed to export report');
    }
  };

  const handleBackfillData = async () => {
    setIsBackfilling(true);
    try {
      const response = await apiClient.post('/wms/sfg/put-away-entries/backfill');
      if (response.data.success) {
        toast.success(`Backfill complete: ${response.data.updated_count} entries updated`);
        fetchEntries(); // Refresh the data
      }
    } catch (error) {
      console.error('Error backfilling data:', error);
      toast.error('Failed to backfill data');
    } finally {
      setIsBackfilling(false);
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'STORED':
        return <Badge className="bg-green-100 text-green-800">STORED</Badge>;
      case 'PICKED':
        return <Badge className="bg-blue-100 text-blue-800">PICKED</Badge>;
      case 'IN_WIP':
        return <Badge className="bg-yellow-100 text-yellow-800">IN WIP</Badge>;
      case 'DELETED':
        return <Badge variant="destructive">DELETED</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  const getSourceBadge = (source) => {
    if (!source) return <Badge variant="outline">-</Badge>;
    
    const sourceLower = source.toLowerCase();
    
    if (sourceLower.includes('goods transfer') || sourceLower.includes('goods_transfer') || sourceLower === 'gt') {
      return <Badge className="bg-purple-100 text-purple-800">GT</Badge>;
    }
    if (sourceLower.includes('dispatch') || sourceLower.includes('sfg')) {
      return <Badge className="bg-blue-100 text-blue-800">SFG</Badge>;
    }
    if (sourceLower.includes('fg') || sourceLower.includes('finished')) {
      return <Badge className="bg-orange-100 text-orange-800">FG</Badge>;
    }
    
    // Default - show abbreviated source
    const abbrev = source.length > 10 ? source.substring(0, 10) + '...' : source;
    return <Badge variant="outline">{abbrev}</Badge>;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    try {
      const date = new Date(dateStr);
      return date.toLocaleString('en-IN', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return dateStr;
    }
  };

  return (
    <div className="min-h-screen bg-[#FFFBF5]">
      <header className="bg-[#1A1F2E] text-[#F9FAFB] shadow-lg">
        <div className="max-w-7xl mx-auto px-4 md:px-6 h-16 flex items-center justify-between">
          <div className="flex items-center">
            <Button
              variant="ghost"
              onClick={() => navigate('/wms/sfg')}
              className="text-[#F9FAFB] hover:bg-white/10 mr-4"
              data-testid="back-btn"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div className="flex items-center gap-3">
              <Package className="w-8 h-8" />
              <div>
                <h1 className="text-xl font-bold font-display">SFG Put Away Entries</h1>
                <p className="text-sm text-[#E8DCC8]">Admin: View & delete put away entries</p>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              onClick={handleBackfillData}
              disabled={isBackfilling}
              className="bg-white/10 border-white/20 text-white hover:bg-white/20"
              data-testid="backfill-data-btn"
            >
              <Database className={`w-4 h-4 mr-2 ${isBackfilling ? 'animate-pulse' : ''}`} />
              {isBackfilling ? 'Backfilling...' : 'Backfill Data'}
            </Button>
            <Button
              variant="outline"
              onClick={handleExportCSV}
              className="bg-white/10 border-white/20 text-white hover:bg-white/20"
              data-testid="export-csv-btn"
            >
              <Download className="w-4 h-4 mr-2" />
              Export
            </Button>
            {selectedEntries.size > 0 && (
              <Button
                variant="destructive"
                onClick={handleBulkDeleteClick}
                data-testid="bulk-delete-btn"
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Delete ({selectedEntries.size})
              </Button>
            )}
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 md:px-6 py-6">
        {/* Filters */}
        <Card className="mb-6">
          <CardHeader className="py-3 px-4">
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg flex items-center gap-2">
                <Filter className="w-5 h-5" />
                Filters
              </CardTitle>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowFilters(!showFilters)}
                >
                  {showFilters ? 'Hide' : 'Show'}
                </Button>
                <Button
                  onClick={handleSearch}
                  size="sm"
                  className="bg-[#2D5F4F] hover:bg-[#234A3D]"
                  disabled={isLoading}
                  data-testid="search-btn"
                >
                  {isLoading ? <RefreshCw className="w-4 h-4 animate-spin mr-2" /> : <Search className="w-4 h-4 mr-2" />}
                  Search
                </Button>
              </div>
            </div>
          </CardHeader>
          {showFilters && (
            <CardContent className="pt-0 pb-4">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                <div>
                  <label className="text-sm font-medium text-gray-700">Bundle Code</label>
                  <Input
                    placeholder="Search bundle..."
                    value={filters.bundle_code}
                    onChange={(e) => setFilters(prev => ({ ...prev, bundle_code: e.target.value }))}
                    data-testid="filter-bundle"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700">Bin Code</label>
                  <Input
                    placeholder="Search bin..."
                    value={filters.bin_code}
                    onChange={(e) => setFilters(prev => ({ ...prev, bin_code: e.target.value }))}
                    data-testid="filter-bin"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700">Design Name</label>
                  <Input
                    placeholder="Search design..."
                    value={filters.design_name}
                    onChange={(e) => setFilters(prev => ({ ...prev, design_name: e.target.value }))}
                    data-testid="filter-design"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700">SKU Code</label>
                  <Input
                    placeholder="Search SKU..."
                    value={filters.barcode}
                    onChange={(e) => setFilters(prev => ({ ...prev, barcode: e.target.value }))}
                    data-testid="filter-barcode"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div>
                  <label className="text-sm font-medium text-gray-700">Date From</label>
                  <Input
                    type="date"
                    value={filters.date_from}
                    onChange={(e) => setFilters(prev => ({ ...prev, date_from: e.target.value }))}
                    data-testid="filter-date-from"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700">Date To</label>
                  <Input
                    type="date"
                    value={filters.date_to}
                    onChange={(e) => setFilters(prev => ({ ...prev, date_to: e.target.value }))}
                    data-testid="filter-date-to"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700">Status</label>
                  <Select
                    value={filters.status}
                    onValueChange={(value) => setFilters(prev => ({ ...prev, status: value }))}
                  >
                    <SelectTrigger data-testid="filter-status">
                      <SelectValue placeholder="All Statuses" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Statuses</SelectItem>
                      <SelectItem value="STORED">STORED</SelectItem>
                      <SelectItem value="ALLOCATED">ALLOCATED</SelectItem>
                      <SelectItem value="PICKED">PICKED</SelectItem>
                      <SelectItem value="IN_WIP">IN WIP</SelectItem>
                      <SelectItem value="PROCESSED">PROCESSED</SelectItem>
                      <SelectItem value="TRANSFERRED">TRANSFERRED</SelectItem>
                      <SelectItem value="MISSING">MISSING</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700">Source</label>
                  <Select
                    value={filters.source}
                    onValueChange={(value) => setFilters(prev => ({ ...prev, source: value }))}
                  >
                    <SelectTrigger data-testid="filter-source">
                      <SelectValue placeholder="All Sources" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Sources</SelectItem>
                      <SelectItem value="GT">Goods Transfer (GT)</SelectItem>
                      <SelectItem value="SFG">Dispatch (SFG)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex items-end">
                  <Button
                    variant="outline"
                    onClick={handleClearFilters}
                    className="w-full"
                  >
                    <X className="w-4 h-4 mr-2" />
                    Clear
                  </Button>
                </div>
              </div>
            </CardContent>
          )}
        </Card>

        {/* Summary */}
        <div className="flex justify-between items-center mb-4">
          <div className="text-sm text-gray-600">
            Showing {((currentPage - 1) * pageSize) + 1} - {Math.min(currentPage * pageSize, totalCount)} of {totalCount} entries
          </div>
          {selectedEntries.size > 0 && (
            <Badge variant="outline" className="text-blue-600 border-blue-600">
              {selectedEntries.size} selected
            </Badge>
          )}
        </div>

        {/* Table */}
        <Card>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-gray-50">
                    <TableHead className="w-12">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={handleSelectAll}
                        className="p-0 h-auto"
                      >
                        {selectedEntries.size === entries.length && entries.length > 0 ? (
                          <CheckSquare className="w-5 h-5 text-blue-600" />
                        ) : (
                          <Square className="w-5 h-5 text-gray-400" />
                        )}
                      </Button>
                    </TableHead>
                    <TableHead className="font-semibold">Bundle Code</TableHead>
                    <TableHead className="font-semibold">Source</TableHead>
                    <TableHead className="font-semibold">Bin</TableHead>
                    <TableHead className="font-semibold">SKU</TableHead>
                    <TableHead className="font-semibold">Design</TableHead>
                    <TableHead className="font-semibold">Size</TableHead>
                    <TableHead className="font-semibold">Shade</TableHead>
                    <TableHead className="font-semibold text-right">Qty</TableHead>
                    <TableHead className="font-semibold">Status</TableHead>
                    <TableHead className="font-semibold">Put Away Date</TableHead>
                    <TableHead className="font-semibold">Put Away By</TableHead>
                    <TableHead className="font-semibold text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {isLoading ? (
                    <TableRow>
                      <TableCell colSpan={13} className="text-center py-8">
                        <RefreshCw className="w-6 h-6 animate-spin mx-auto mb-2" />
                        Loading...
                      </TableCell>
                    </TableRow>
                  ) : entries.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={13} className="text-center py-8 text-gray-500">
                        No entries found. Try adjusting your filters.
                      </TableCell>
                    </TableRow>
                  ) : (
                    entries.map((entry, index) => (
                      <TableRow key={index} className={`hover:bg-gray-50 ${entry.is_deleted ? 'bg-red-50' : ''}`}>
                        <TableCell>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleSelectEntry(entry.bundle_code)}
                            className="p-0 h-auto"
                          >
                            {selectedEntries.has(entry.bundle_code) ? (
                              <CheckSquare className="w-5 h-5 text-blue-600" />
                            ) : (
                              <Square className="w-5 h-5 text-gray-400" />
                            )}
                          </Button>
                        </TableCell>
                        <TableCell className="font-mono text-xs">{entry.bundle_code}</TableCell>
                        <TableCell>{getSourceBadge(entry.source_section)}</TableCell>
                        <TableCell className="font-mono text-sm">{entry.bin_location || entry.bin_code || '-'}</TableCell>
                        <TableCell className="font-mono text-xs">{entry.sku_code}</TableCell>
                        <TableCell className="max-w-[150px] truncate">{entry.design_name || '-'}</TableCell>
                        <TableCell>{entry.size || '-'}</TableCell>
                        <TableCell>{entry.shade || '-'}</TableCell>
                        <TableCell className="text-right font-semibold">{entry.usn_count || entry.case_pack_size || '-'}</TableCell>
                        <TableCell>{getStatusBadge(entry.status)}</TableCell>
                        <TableCell className="text-sm">{formatDate(entry.put_away_date)}</TableCell>
                        <TableCell className="text-sm">{entry.put_away_by_name || entry.put_away_by || '-'}</TableCell>
                        <TableCell className="text-right">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDeleteClick(entry)}
                            className="text-red-600 hover:text-red-700 hover:bg-red-50"
                            data-testid={`delete-${entry.bundle_code}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
            
            {/* Pagination Controls */}
            {totalPages > 1 && (
              <div className="flex items-center justify-between px-4 py-3 border-t">
                <div className="text-sm text-gray-600">
                  Page {currentPage} of {totalPages} ({totalCount} total entries)
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handlePrevPage}
                    disabled={currentPage === 1}
                    data-testid="prev-page-btn"
                  >
                    Previous
                  </Button>
                  <span className="px-3 py-1 bg-gray-100 rounded text-sm font-medium">
                    {currentPage}
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleNextPage}
                    disabled={currentPage >= totalPages}
                    data-testid="next-page-btn"
                  >
                    Next
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </main>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <AlertTriangle className="w-5 h-5" />
              Confirm Delete
            </DialogTitle>
            <DialogDescription>
              {entryToDelete ? (
                <>Are you sure you want to delete entry <strong>{entryToDelete.bundle_code}</strong>?</>
              ) : (
                <>Are you sure you want to delete <strong>{selectedEntries.size}</strong> selected entries?</>
              )}
              <br /><br />
              <span className="text-red-600">This action will archive the entries and remove them from active inventory.</span>
            </DialogDescription>
          </DialogHeader>
          
          {entryToDelete && (
            <div className="bg-gray-50 p-3 rounded-lg text-sm">
              <p><strong>Bundle:</strong> {entryToDelete.bundle_code}</p>
              <p><strong>Bin:</strong> {entryToDelete.bin_code}</p>
              <p><strong>SKU:</strong> {entryToDelete.sku_code}</p>
              <p><strong>Status:</strong> {entryToDelete.status}</p>
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialogOpen(false)} disabled={isDeleting}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleConfirmDelete}
              disabled={isDeleting}
              data-testid="confirm-delete-btn"
            >
              {isDeleting ? (
                <>
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  Deleting...
                </>
              ) : (
                <>
                  <Trash2 className="w-4 h-4 mr-2" />
                  Delete
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SFGPutAwayEntriesReport;
